import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { EGIconButton } from "../../../components/MainComponents/Button/IconButton/IconButton";
import Icon from "../../../components/Icon/Icon";
import { initialCurrentReportLS } from "../../../components/Report/All/ReportViewer";
import { useReportContext } from "../../../components/Report/ReportContext/useReportContext";
import { HighlightText } from "../HighlightText";
import {
  useInvolvedColors,
  useInvolvedTypeLabel,
} from "../../../hooks/useInvolvedDisplay";
import {
  formatResultDate,
  GroupLabel,
  KeyTag,
  MetaText,
  resultActionsSx,
  resultDividerSx,
  resultRowSx,
  resultTitleSx,
  StatusTag,
} from "./resultBits";

/** Colores por estado de investigación, alineados con el resto del panel */
const useStatusStyles = () => {
  const theme = useTheme();
  const [t] = useTranslation("report");

  return {
    NEW: { label: t("investigationStatus.new"), color: theme.palette.blue.main },
    PENDING: {
      label: t("investigationStatus.pending"),
      color: theme.palette.purple.main,
    },
    PROCESS: {
      label: t("investigationStatus.process"),
      color: theme.palette.success.main,
    },
    COMPLETE: {
      label: t("investigationStatus.complete"),
      color: theme.palette.warning.main,
    },
  };
};

/**
 * Secciones del folder. Sirven para dos cosas: el conteo (siempre disponible,
 * el endpoint dice cuántos elementos hay sin descifrar nada) y el fragmento
 * donde empató la búsqueda, que solo llega cuando el cliente guarda esas
 * secciones sin cifrar.
 */
const SECTION_ICONS = {
  involved: "outline_profile_2_user",
  interviews: "outline_messages_3",
  evidences: "paperclip",
  plans: "outline_device_message",
};

/**
 * Una denuncia ligada con su contexto: estatus (y tipo de cierre si ya cerró),
 * folio, canal, severidad y fecha. Toda la fila lleva a la denuncia.
 */
const LinkedReport = ({ report, query, reportContext }) => {
  const [t] = useTranslation("report");
  const [tExports] = useTranslation("exports");
  const navigate = useNavigate();
  const { status_locales, entryways, severityOptions } = reportContext;

  const status = status_locales[report.status] || {};
  // El tipo de cierre solo existe en denuncias cerradas; sin él aún no se
  // determina, igual que en el Builder.
  const closedLabel =
    report.status === "closed"
      ? report.closed_type
        ? tExports(`builder.closed_${report.closed_type}`, report.closed_type)
        : t("searchBar.linked_report_closed_in_process")
      : null;
  // "type::None" es el valor por defecto: no aporta nada mostrarlo
  const severity = severityOptions.find((o) => o.value === report.severity)?.label;
  const details = [
    entryways[report.entryway]?.label,
    severity && `${t("searchBar.linked_report_severity")}: ${severity}`,
    report.created_at &&
      `${t("searchBar.linked_report_opened")} ${formatResultDate(report.created_at)}`,
  ].filter(Boolean);

  return (
    <Stack
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/reports/${report.id}`)}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/reports/${report.id}`)}
      sx={{
        gap: 0.15,
        cursor: "pointer",
        width: "fit-content",
        maxWidth: "100%",
        "&:hover .linked-report-folio": { textDecoration: "underline" },
      }}
    >
      {/* Estatus y folio juntos: es lo que se busca al barrer la lista */}
      <Stack
        sx={{ flexFlow: "row wrap", alignItems: "center", columnGap: 1, rowGap: 0.25 }}
      >
        <StatusTag
          label={closedLabel ? `${status.label} · ${closedLabel}` : status.label}
          color={status.color}
        />
        <Typography
          className="linked-report-folio"
          variant="body2"
          fontSize={12}
          fontWeight={600}
          color="primary"
        >
          <HighlightText text={report.folio || ""} query={query} />
        </Typography>
      </Stack>
      <MetaText items={details} />
    </Stack>
  );
};

export const InvestigationResults = ({ results = [], query = "" }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const involvedColors = useInvolvedColors();
  const involvedTypeLabel = useInvolvedTypeLabel();
  const navigate = useNavigate();
  const statusStyles = useStatusStyles();
  // Una sola vez para toda la lista: el contexto reconstruye sus catálogos en
  // cada llamada.
  const reportContext = useReportContext();

  /**
   * Las investigaciones no tienen ruta propia: viven en la pestaña
   * "Investigación" de ReportViewer, que lee la pestaña activa y la
   * investigación abierta desde localStorage. Se siembra antes de navegar para
   * caer directo en la investigación buscada.
   */
  const openInvestigation = (investigation, reportId) => {
    localStorage.setItem(
      "current-report",
      JSON.stringify({
        ...initialCurrentReportLS(reportId),
        tab: "Investigación",
        "inv-alias": investigation.alias,
        "inv-id": investigation.id,
      })
    );
    navigate(`/reports/${reportId}`);
  };

  return (
    <Stack divider={<Divider sx={resultDividerSx(theme)} />}>
      {results.map((investigation) => {
        const status = statusStyles[investigation.status] || {};
        const reports = investigation.reports || [];
        const primaryReport = reports[0];
        const sections = investigation.sections || {};
        // Dónde empató el término dentro del folder, con su fragmento
        const matches = investigation.matches || [];
        // La conclusión en claro es la columna; folder.conclusion está cifrado
        const excerpt = investigation.conclusion || investigation.comment;

        return (
          <Stack key={investigation.id} sx={resultRowSx(theme)}>
            {/* Contexto: estado · tipo · fecha, y el alias al margen */}
            <Stack
              sx={{
                flexFlow: "row nowrap",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <StatusTag label={status.label} color={status.color} />
              <MetaText
                items={[
                  investigation.advance
                    ? t("searchBar.investigation_advance")
                    : t("searchBar.investigation_simple"),
                  formatResultDate(investigation.created_at),
                ]}
              />
              {/* El alias es una cadena de 34 caracteres: identifica, pero no
                  merece pelear con el título. Va al margen y recortado, con el
                  valor completo en el tooltip. */}
              <KeyTag
                value={investigation.alias}
                title={t("searchBar.result_alias")}
              >
                <HighlightText text={investigation.alias || ""} query={query} />
              </KeyTag>
            </Stack>

            {/* Título */}
            <Typography
              color={primaryReport ? "primary" : "text.primary"}
              sx={{
                ...resultTitleSx,
                cursor: primaryReport ? "pointer" : "default",
                "&:hover": primaryReport ? { textDecoration: "underline" } : {},
              }}
              onClick={() =>
                primaryReport &&
                openInvestigation(investigation, primaryReport.id)
              }
            >
              <HighlightText
                text={investigation.title || t("searchBar.investigation_no_title")}
                query={query}
              />
            </Typography>

            {/* Si el término apareció dentro del folder se muestra ahí, que es
                lo que explica el resultado; si no, el resumen de siempre. */}
            {matches.length > 0 ? (
              <Stack sx={{ mt: 0.5, gap: 0.25 }}>
                {matches.map((match) => (
                  <Stack
                    key={`${match.section}-${match.role || ""}`}
                    direction="row"
                    spacing={0.75}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <Icon
                      name={SECTION_ICONS[match.section]}
                      size={14}
                      color={
                        involvedColors[match.role] ||
                        theme.palette.blueGray500.main
                      }
                      style={{ marginTop: 3, flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: "min(100%, 90ch)",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          mr: 0.5,
                          fontWeight: 600,
                          color: "text.primary",
                          textTransform: "capitalize",
                        }}
                      >
                        {t(`searchBar.section_${match.section}`)}
                        {match.role && (
                          <Box
                            component="span"
                            sx={{ color: involvedColors[match.role] }}
                          >
                            {` · ${involvedTypeLabel(match.role)}`}
                          </Box>
                        )}
                        :
                      </Box>
                      <HighlightText text={match.excerpt} query={query} />
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              excerpt && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    maxWidth: "min(100%, 90ch)",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  <HighlightText text={excerpt} query={query} />
                </Typography>
              )
            )}

            {/* Denuncias ligadas, cada una con su estatus y contexto */}
            {reports.length > 0 && (
              <Stack sx={{ mt: 0.5, gap: 0.75 }}>
                <GroupLabel>{t("searchBar.linked_reports")}</GroupLabel>
                {reports.map((report) => (
                  <LinkedReport
                    key={report.id}
                    report={report}
                    query={query}
                    reportContext={reportContext}
                  />
                ))}
              </Stack>
            )}

            {/* Conteo de secciones: se sabe cuántas hay sin descifrar contenido */}
            <Stack
              sx={{
                mt: 1,
                flexFlow: "row wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                minHeight: 32,
              }}
            >
              <Stack direction="row" spacing={1.75} alignItems="center" flexWrap="wrap">
                {Object.entries(SECTION_ICONS).map(([section, iconName]) =>
                  sections[section] > 0 ? (
                    <Stack
                      key={section}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                    >
                      <Icon
                        name={iconName}
                        size={15}
                        color={theme.palette.blueGray500.main}
                      />
                      <Typography variant="body2" fontSize={11} color="text.disabled">
                        {t(`searchBar.count_${section}`, {
                          count: sections[section],
                        })}
                      </Typography>
                    </Stack>
                  ) : null
                )}
              </Stack>

              {primaryReport && (
                <Box className="search-result-actions" sx={resultActionsSx}>
                  <EGIconButton
                    title={t("searchBar.open_investigation")}
                    iconName="outline_eye"
                    iconColor="primary"
                    iconSize={18}
                    onClick={() =>
                      openInvestigation(investigation, primaryReport.id)
                    }
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};
